import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  FC,
} from "react";
import firebaseApp from "./firebase";
import {
  getAuth,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  User,
} from "firebase/auth";
import { setAuthContextRef } from "../api/apiService";

export const firebaseAuth = getAuth(firebaseApp);

const AuthContext = React.createContext<{
  currentUser: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  token: string | null;
  sendEmailVerification: () => Promise<boolean>;
  emailVerified: boolean;
  checkValidToken: () => void;
}>({
  currentUser: null,
  loading: true,
  logout: async () => {},
  token: null,
  sendEmailVerification: async () => false,
  emailVerified: false,
  checkValidToken: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [token, setToken] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState<boolean>(false);

  function logout() {
    return signOut(firebaseAuth);
  }

  const sendEmailVerificationInner = useCallback(async () => {
    if (currentUser) {
      await sendEmailVerification(currentUser, {
        url: window.location.origin + "/",
      });
    }
    return true;
  }, [currentUser]);

  // Single useEffect to handle all auth state changes and token management
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
      setCurrentUser(user);
      setLoading(false);

      if (user) {
        try {
          // Get the current token when user state changes
          const idToken = await user.getIdToken(false);
          setToken(idToken);
          setEmailVerified(user.emailVerified);
        } catch (error) {
          console.error("Error getting token:", error);
          setToken(null);
          setEmailVerified(false);
        }
      } else {
        setToken(null);
        setEmailVerified(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Automatic token refresh every 50 minutes (before 1-hour expiry)
  useEffect(() => {
    if (!currentUser) return;

    const refreshInterval = setInterval(async () => {
      try {
        console.log("Auto-refreshing Firebase token...");
        const idToken = await currentUser.getIdToken(true); // Force refresh
        setToken(idToken);
        console.log("Token refreshed successfully");
      } catch (error) {
        console.error("Error auto-refreshing token:", error);
        // If refresh fails, logout user
        await logout();
      }
    }, 50 * 60 * 1000); // Refresh every 50 minutes

    return () => clearInterval(refreshInterval);
  }, [currentUser]);

  // Simple token refresh function for manual use
  const checkValidToken = useCallback(async () => {
    if (!currentUser) return;

    try {
      const idToken = await currentUser.getIdToken(true);
      setToken(idToken);
    } catch (error) {
      console.error("Error refreshing token:", error);
      await logout();
    }
  }, [currentUser]);

  const value = useMemo(
    () => ({
      currentUser,
      loading,
      logout,
      token,
      sendEmailVerification: sendEmailVerificationInner,
      emailVerified,
      checkValidToken,
    }),
    [
      currentUser,
      loading,
      token,
      emailVerified,
      checkValidToken,
      sendEmailVerificationInner,
    ]
  );

  // Register this auth context with the API service for token refresh
  useEffect(() => {
    setAuthContextRef(value);
  }, [value]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
